import Page from '@rhg/components/page';
import { AppConfigProvider, extractAppConfig } from '@rhg/hooks/use-app-config';
import Auth0Provider from '@rhg/libs/auth0/auth0-provider';
import { queryClient, QueryClientProvider } from '@rhg/libs/react-query';
import { ReactQueryDevtools } from '@rhg/libs/react-query/devtools';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { ReactElement, ReactNode, useMemo } from 'react';
import '@rhg/libs/datadog';
import { CookieResponseDialog } from '@rhg/components/cookie-dialog';
import ErrorBoundary from '@rhg/components/error-boundary';
import HeartBeat from '@rhg/components/heartbeat';
import { RegistrationDialogManager } from '@rhg/components/registration/dialog/manager';
import { UserBlockerGate } from '@rhg/components/user-blocker-gate';
import { Environment } from '@rhg/constants';
import { AnalyticsProvider, TrackDeviceType } from '@rhg/libs/analytics';
import { createDestinations } from '@rhg/libs/analytics/destination';
import trackWebVitals from '@rhg/libs/analytics/track-web-vitals';
import { AccessCodeProvider } from '@rhg/providers/access-code';
import { ActiveCircleIdProvider } from '@rhg/providers/active-circle-id';
import { AudioProvider } from '@rhg/providers/audio';
import { AudioPlayerProvider } from '@rhg/providers/audio-player';
import { AutoSelectCircleProvider } from '@rhg/providers/auto-select-circle';
import { ChoiceDialogProvider } from '@rhg/providers/choice-dialog';
import { CircleJwtProvider } from '@rhg/providers/circle-jwt';
import { CookieResponseProvider } from '@rhg/providers/cookie-response';
import { DeepLinkGuideManager } from '@rhg/providers/deeplink-guide-manager';
import { EnforcedThemeProvider } from '@rhg/providers/enforced-theme-provider';
import { GuidePlayerProvider } from '@rhg/providers/guide-player';
import { HistoryProvider } from '@rhg/providers/history';
import { IntlWrapper } from '@rhg/providers/intl-wrapper';
import { InviteCodeProvider } from '@rhg/providers/invite-code';
import { LoadingDialogProvider } from '@rhg/providers/loading-dialog';
import MediaPreferencesProvider from '@rhg/providers/media-preferences';
import { SnackbarProvider } from '@rhg/providers/snackbar';
import { StandaloneResultsValuesProvider } from '@rhg/providers/standalone-results-values';
import { ThemeSwitchProvider } from '@rhg/providers/theme-switch';
import { ToastDialogProvider } from '@rhg/providers/toast-dialog';
import { VideoPlayerProvider } from '@rhg/providers/video-player';
import { WorkflowProvider } from '@rhg/providers/workflow';
import { NextPage } from 'next';

const {
    NEXT_PUBLIC_ENV: env,
    NEXT_PUBLIC_AMPLITUDE_BASEURL: amplitudeBaseUrl,
    NEXT_PUBLIC_AMPLITUDE_TOKEN: amplitudeToken,
    NEXT_PUBLIC_ANALYTICS_DEBUG: debug,
} = extractAppConfig();

const destinations = createDestinations({
    amplitudeToken,
    amplitudeBaseUrl,
    env: env as Environment,
    debug,
});

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
    const appConfig = useMemo(extractAppConfig, []);

    const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

    return (
        <HistoryProvider>
            <AppConfigProvider value={appConfig}>
                <QueryClientProvider client={queryClient}>
                    <CookieResponseProvider>
                        <StandaloneResultsValuesProvider>
                            <Auth0Provider>
                                <AnalyticsProvider destinations={destinations}>
                                    <TrackDeviceType>
                                        <IntlWrapper>
                                            <EnforcedThemeProvider>
                                                <ThemeSwitchProvider>
                                                    <ActiveCircleIdProvider>
                                                        <ErrorBoundary
                                                            appConfig={
                                                                appConfig
                                                            }
                                                        >
                                                            <CookieResponseDialog />
                                                            <CircleJwtProvider>
                                                                <AutoSelectCircleProvider>
                                                                    <AccessCodeProvider>
                                                                        <MediaPreferencesProvider>
                                                                            <InviteCodeProvider>
                                                                                <SnackbarProvider>
                                                                                    <LoadingDialogProvider>
                                                                                        <ToastDialogProvider>
                                                                                            <ChoiceDialogProvider>
                                                                                                <AudioProvider>
                                                                                                    <AudioPlayerProvider>
                                                                                                        <VideoPlayerProvider>
                                                                                                            <GuidePlayerProvider>
                                                                                                                <HeartBeat />
                                                                                                                <WorkflowProvider>
                                                                                                                    <UserBlockerGate>
                                                                                                                        <DeepLinkGuideManager />
                                                                                                                        <Page
                                                                                                                            {...pageProps}
                                                                                                                        >
                                                                                                                            {getLayout(
                                                                                                                                <Component
                                                                                                                                    {...pageProps}
                                                                                                                                />
                                                                                                                            )}
                                                                                                                        </Page>
                                                                                                                        <RegistrationDialogManager />
                                                                                                                    </UserBlockerGate>
                                                                                                                </WorkflowProvider>
                                                                                                            </GuidePlayerProvider>
                                                                                                        </VideoPlayerProvider>
                                                                                                    </AudioPlayerProvider>
                                                                                                </AudioProvider>
                                                                                            </ChoiceDialogProvider>
                                                                                        </ToastDialogProvider>
                                                                                    </LoadingDialogProvider>
                                                                                </SnackbarProvider>
                                                                            </InviteCodeProvider>
                                                                        </MediaPreferencesProvider>
                                                                    </AccessCodeProvider>
                                                                </AutoSelectCircleProvider>
                                                            </CircleJwtProvider>
                                                        </ErrorBoundary>
                                                    </ActiveCircleIdProvider>
                                                    <ReactQueryDevtools
                                                        buttonPosition="bottom-left"
                                                        initialIsOpen={false}
                                                    />
                                                </ThemeSwitchProvider>
                                            </EnforcedThemeProvider>
                                        </IntlWrapper>
                                    </TrackDeviceType>
                                </AnalyticsProvider>
                            </Auth0Provider>
                        </StandaloneResultsValuesProvider>
                    </CookieResponseProvider>
                </QueryClientProvider>
            </AppConfigProvider>
        </HistoryProvider>
    );
};

export const reportWebVitals = (metric: NextWebVitalsMetric) => {
    trackWebVitals(metric, destinations);
};

export default App;
